import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { getMfePopover, hideMfePopover } from '@/features/microfrontends/slice/mfePopoverSlice';

const MfePopover = () => {
    const dispatch = useAppDispatch();
    const { iFrameId, isOpen, title, html, triggerRect } = useAppSelector(getMfePopover);
    const popoverRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (!isOpen || !triggerRect || !iFrameId) {
            setPosition(null);
            return;
        }

        // Find the source MFE iframe to calculate the absolute position
        const sourceIframe = Array.from(document.getElementsByTagName('iframe')).find((iframe) => iframe.src.startsWith(iFrameId));

        if (!sourceIframe) {
            setPosition(null);
            return;
        }

        const iframeRect = sourceIframe.getBoundingClientRect();
        // Position: top aligned to the vertical center of the trigger icon
        const top = iframeRect.top + triggerRect.top + triggerRect.height / 2;
        // Position: left at the right edge of the trigger icon
        const left = iframeRect.left + triggerRect.left + triggerRect.width;

        setPosition({ top, left });
    }, [isOpen, triggerRect, iFrameId]);

    useEffect(() => {
        if (!isOpen) return;

        const handleScroll = () => {
            dispatch(hideMfePopover());
        };

        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isOpen, dispatch]);

    if (!isOpen || !position || !html) {
        return null;
    }

    return createPortal(
        <div
            ref={popoverRef}
            className="popover bs-popover-right fade show"
            x-placement="right"
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left + 6}px`,
                transform: 'translateY(-50%)',
                zIndex: 1060,
                maxWidth: '250px',
                minWidth: '220px',
            }}
            onMouseLeave={() => dispatch(hideMfePopover())}
        >
            <div className="arrow" style={{ margin: '0px', position: 'absolute', top: '50%', transform: 'translateY(-50%)' }} />
            {title && <h3 className="popover-title margin-top-0 text-size-16 text-medium">{title}</h3>}
            <div className="popover-content" dangerouslySetInnerHTML={{ __html: html }} />
        </div>,
        document.body
    );
};

export default MfePopover;
